import { MediaContentTypes } from "constants/contentTypes"
import { HyperLinkBuilder } from "widgets/commonComponents/inline/hyperLink"
import { PayWallArctilceIcon } from "widgets/commonComponents/inline/payWallHavak/payWallHavak"
import { LinkedItemFieldDisplay } from "widgets/commonDataObjects/linkedItemsFieldsDisplay"
import { LinkedItemType, VideoSettingsData } from "widgets/commonDataObjects/interfaces"
import { SiteImageData, SiteItemData, SiteMediaData, SiteVideoData } from "widgets/interfaces"
import { HotStoriesMedia } from "./hotStoriesMedia"
import { separateHourFromDateAndFormatToString } from "widgets/widgetsUtils"
import { getDiffFromToday } from "../../../../siteWidgets/formatDateDisplay"
import * as classNames from "classnames"
import { HOT_STORIES_BG, syncHotStoriesTitleSubClamp } from "../hotStoriesConsts"

export interface HotStoriesSiteItem extends SiteItemData {
    buttonTitle?: string
    hideVideoAds?: boolean
}

interface HotStoriesFrontProps {
    items: HotStoriesSiteItem[]
    width: number
    videoSettings: VideoSettingsData
    fieldsDisplaySettings: LinkedItemFieldDisplay
    gallerySeconds: number
    backgroundColorComponent?: string
    backgroundColorRoof?: string
    backgroundColorLabel?: string
}

interface HotStoriesFrontState {
    activeIndex: number
    isMuted: boolean
    autoplayEnabled: boolean
    progressKey: number
}

export class HotStoriesFront extends React.Component<HotStoriesFrontProps, HotStoriesFrontState> {
    public static siteScriptName = "HotStoriesFront"

    private autoTimer: any
    private mediaWidth = 796
    private mediaHeight = 448
    private progressPollTimer: any = null
    private mediaRootRef: HTMLDivElement | null = null
    private videoProgressFillRef: HTMLDivElement | null = null
    private lastProgressRatio = 0
    private titleClampRaf: number | null = null
    private copyRootRef: HTMLDivElement | null = null

    constructor(props: HotStoriesFrontProps) {
        super(props)
        this.state = {
            activeIndex: 0,
            isMuted: true,
            autoplayEnabled: props.gallerySeconds > 0,
            progressKey: 0,
        }
    }

    componentDidMount() {
        this.scheduleAutoAdvance()
        this.startVideoProgressPoll()
        this.scheduleTitleSubClamp()
        window.YitVideo && window.YitVideo.InfrastructureReInitialize()
    }

    componentDidUpdate(_: HotStoriesFrontProps, prevState: HotStoriesFrontState) {
        if (prevState.activeIndex !== this.state.activeIndex) {
            this.stopVideoProgressPoll()
            this.lastProgressRatio = 0
            window.YitVideo && window.YitVideo.InfrastructureReInitialize()
            this.scheduleAutoAdvance()
            this.startVideoProgressPoll()
            this.scheduleTitleSubClamp()
        }
    }

    componentWillUnmount() {
        this.clearAutoAdvance()
        this.stopVideoProgressPoll()
        if (this.titleClampRaf !== null && typeof window !== "undefined") {
            window.cancelAnimationFrame(this.titleClampRaf)
            this.titleClampRaf = null
        }
    }

    private clearAutoAdvance = () => {
        if (this.autoTimer) {
            clearTimeout(this.autoTimer)
            this.autoTimer = null
        }
    }

    private stopAutoplay = () => {
        this.clearAutoAdvance()
        if (this.state.autoplayEnabled) {
            this.setState({ autoplayEnabled: false })
        }
    }

    private isVideoItem = (item: HotStoriesSiteItem) => {
        const media = item && item.media
        if (!media) {
            return false
        }
        return media.mediaType === MediaContentTypes.MEDIA_VIDEO
    }

    private getAdvanceMs = (item: HotStoriesSiteItem) => {
        const { gallerySeconds } = this.props
        if (!gallerySeconds || gallerySeconds <= 0) {
            return 0
        }
        if (this.isVideoItem(item)) {
            return 0
        }
        return gallerySeconds * 1000
    }

    private scheduleAutoAdvance = () => {
        this.clearAutoAdvance()
        const { items } = this.props
        const { activeIndex, autoplayEnabled } = this.state
        if (!autoplayEnabled || !items || items.length < 2) {
            return
        }
        if (this.isVideoItem(items[activeIndex])) {
            return
        }
        const ms = this.getAdvanceMs(items[activeIndex])
        if (ms <= 0) {
            return
        }
        this.autoTimer = setTimeout(() => {
            this.goToIndex((activeIndex + 1) % items.length, false)
        }, ms)
    }

    private advanceFromVideo = () => {
        const { items } = this.props
        const { activeIndex, autoplayEnabled } = this.state
        if (!autoplayEnabled || !items || items.length < 2) {
            return
        }
        if (!this.isVideoItem(items[activeIndex])) {
            return
        }
        this.goToIndex((activeIndex + 1) % items.length, false)
    }

    private goToIndex = (index: number, stopAuto: boolean) => {
        if (stopAuto) {
            this.stopAutoplay()
        }
        this.stopVideoProgressPoll()
        this.lastProgressRatio = 0
        this.setState(state => ({
            activeIndex: index,
            progressKey: state.progressKey + 1,
            autoplayEnabled: stopAuto ? false : state.autoplayEnabled,
        }))
    }

    private onSelectItem = (index: number) => {
        if (index === this.state.activeIndex) {
            return
        }
        this.goToIndex(index, true)
    }

    private onPrev = () => {
        const { items } = this.props
        const { activeIndex } = this.state
        if (!items || activeIndex <= 0) {
            return
        }
        this.goToIndex(activeIndex - 1, true)
    }

    private onNext = () => {
        const { items } = this.props
        const { activeIndex } = this.state
        if (!items || activeIndex >= items.length - 1) {
            return
        }
        this.goToIndex(activeIndex + 1, true)
    }

    private onSharedUnmute = () => {
        if (this.state.isMuted) {
            this.setState({ isMuted: false })
        }
    }

    private setMediaRoot = (el: HTMLDivElement | null) => {
        this.mediaRootRef = el
    }

    private setCopyRoot = (el: HTMLDivElement | null) => {
        this.copyRootRef = el
        this.scheduleTitleSubClamp()
    }

    private scheduleTitleSubClamp = () => {
        if (typeof window === "undefined") {
            return
        }
        if (this.titleClampRaf !== null) {
            window.cancelAnimationFrame(this.titleClampRaf)
        }
        this.titleClampRaf = window.requestAnimationFrame(() => {
            this.titleClampRaf = window.requestAnimationFrame(() => {
                this.titleClampRaf = null
                this.syncTitleSubClamp()
            })
        })
    }

    private syncTitleSubClamp = () => {
        syncHotStoriesTitleSubClamp(this.copyRootRef)
    }

    private setVideoProgressFill = (el: HTMLDivElement | null) => {
        this.videoProgressFillRef = el
        if (el) {
            el.style.width = `${Math.round(this.lastProgressRatio * 1000) / 10}%`
        }
    }

    private stopVideoProgressPoll = () => {
        if (this.progressPollTimer) {
            clearInterval(this.progressPollTimer)
            this.progressPollTimer = null
        }
    }

    private findActiveVideoEl = (): HTMLVideoElement | null => {
        const root = this.mediaRootRef
        if (!root) {
            return null
        }
        const videos = root.querySelectorAll("video")
        if (!videos || videos.length === 0) {
            return null
        }
        for (let i = 0; i < videos.length; i++) {
            const v = videos[i] as HTMLVideoElement
            if (!v.paused && v.duration > 0 && isFinite(v.duration)) {
                return v
            }
        }
        return videos[videos.length - 1] as HTMLVideoElement
    }

    private syncVideoProgressFromDom = () => {
        const fill = this.videoProgressFillRef
        const video = this.findActiveVideoEl()
        if (!fill || !video || !(video.duration > 0) || !isFinite(video.duration)) {
            return
        }
        const time = video.currentTime
        if (!(time >= 0) || !isFinite(time)) {
            return
        }
        const next = Math.min(1, Math.max(0, time / video.duration))
        if (Math.abs(next - this.lastProgressRatio) < 0.005) {
            return
        }
        this.lastProgressRatio = next
        fill.style.width = `${Math.round(next * 1000) / 10}%`
    }

    private startVideoProgressPoll = () => {
        this.stopVideoProgressPoll()
        const { items } = this.props
        const { activeIndex } = this.state
        if (!items || !this.isVideoItem(items[activeIndex])) {
            return
        }
        this.progressPollTimer = setInterval(this.syncVideoProgressFromDom, 250)
    }

    private onPlayStarted = () => {
        this.onSharedUnmute()
        this.clearAutoAdvance()
        if (!this.progressPollTimer) {
            this.startVideoProgressPoll()
        }
    }

    private onVideoEnded = () => {
        this.lastProgressRatio = 1
        if (this.videoProgressFillRef) {
            this.videoProgressFillRef.style.width = "100%"
        }
        this.advanceFromVideo()
    }

    private onVideoError = () => {
        this.advanceFromVideo()
    }

    private formatMeta = (item: HotStoriesSiteItem) => {
        const { fieldsDisplaySettings } = this.props
        const parts: string[] = []
        if (fieldsDisplaySettings.isAuthorVisible && item.author) {
            parts.push(item.author)
        }
        if (fieldsDisplaySettings.isDateVisible && item.itemType === LinkedItemType.ARTICLE && item.dateUpdatedOnSite) {
            const isTodayDate = getDiffFromToday(new Date(item.dateUpdatedOnSite)) === 0
            parts.push(separateHourFromDateAndFormatToString(item.dateUpdatedOnSite, {
                showDate: !isTodayDate,
                showHour: isTodayDate,
            }))
        }
        return parts.join(" | ")
    }

    private getActiveMedia = (item: HotStoriesSiteItem): SiteMediaData => {
        const media = item.media
        if (!media) {
            return { url: "", mediaType: MediaContentTypes.MEDIA_IMAGE, title: "", className: "", alt: "", ariaHidden: true } as SiteImageData
        }
        if (media.mediaType === MediaContentTypes.MEDIA_VIDEO) {
            return {
                ...media,
                isMuteAvailable: this.state.isMuted,
                isSoundButtonVisible: true,
            } as SiteVideoData
        }
        return media
    }

    private renderHero = (item: HotStoriesSiteItem) => {
        const { fieldsDisplaySettings, videoSettings, backgroundColorRoof, backgroundColorLabel } = this.props
        const hasLink = !!(item.titleLink)
        const media = this.getActiveMedia(item)
        const isVideo = media.mediaType === MediaContentTypes.MEDIA_VIDEO
        const credit = (media as SiteImageData).credit
        const roofStyle = backgroundColorRoof ? { backgroundColor: backgroundColorRoof } : undefined
        const labelStyle = backgroundColorLabel ? { backgroundColor: backgroundColorLabel } : undefined

        const titleNode = fieldsDisplaySettings.isTitleVisible && item.title && (
            hasLink
                ? <HyperLinkBuilder className="hotStoriesTitleLink" href={item.titleLink} linkTarget={item.titleTargetLink} articleId={item.itemId}>
                    <h2 className="hotStoriesTitle">{item.title}</h2>
                </HyperLinkBuilder>
                : <h2 className="hotStoriesTitle">{item.title}</h2>
        )

        const subTitleNode = fieldsDisplaySettings.isSubTitleVisible && item.subTitle && (
            hasLink
                ? <HyperLinkBuilder className="hotStoriesSubTitleLink" href={item.titleLink} linkTarget={item.titleTargetLink} articleId={item.itemId}>
                    <p className="hotStoriesSubTitle">{item.subTitle}</p>
                </HyperLinkBuilder>
                : <p className="hotStoriesSubTitle">{item.subTitle}</p>
        )

        const cta = hasLink && item.buttonTitle
            ? <HyperLinkBuilder className="hotStoriesCta" href={item.titleLink} linkTarget={item.titleTargetLink} articleId={item.itemId}>
                <span className="hotStoriesCtaText">{item.buttonTitle.replace(/\s*>\s*$/, "").trim() || item.buttonTitle}</span>
            </HyperLinkBuilder>
            : null

        const meta = this.formatMeta(item)

        return (
            <div className="hotStoriesHero">
                <div className="hotStoriesCopy" ref={this.setCopyRoot}>
                    <div className="hotStoriesArticleHeader">
                        {fieldsDisplaySettings.isRoofTitleVisible && item.roofTitle &&
                            <span className="hotStoriesRoofChip" style={roofStyle}>{item.roofTitle}</span>}
                        <div className="hotStoriesArticleBody">
                            {titleNode}
                            {subTitleNode}
                            {meta && <div className="hotStoriesMeta">{meta}</div>}
                        </div>
                    </div>
                    {cta}
                </div>
                <div className="hotStoriesMedia" ref={this.setMediaRoot}>
                    <HotStoriesMedia
                        key={item.itemId || item.title || String(this.state.activeIndex)}
                        data={media}
                        width={this.mediaWidth}
                        height={this.mediaHeight}
                        hideVideoAds={item.hideVideoAds !== false || !!(videoSettings && videoSettings.hideVideoAds)}
                        isImageLinkable={hasLink && !isVideo}
                        itemLinkUrl={item.titleLink}
                        linkTarget={item.titleTargetLink}
                        articleId={item.itemId}
                        onPlayStarted={this.onPlayStarted}
                        onVideoEnded={this.onVideoEnded}
                        onVideoError={this.onVideoError}
                    />
                    {item.redLabelText &&
                        <span className="hotStoriesMediaLabel" style={labelStyle}>
                            {item.redLabelText}
                        </span>}
                    <PayWallArctilceIcon
                        premiumExpireDays={item.premiumExpireDays}
                        dateUpdatedOnSite={item.dateUpdatedOnSite}
                    />
                    {credit && <div className="hotStoriesCredit">({credit})</div>}
                </div>
            </div>
        )
    }

    private renderStripItem = (item: HotStoriesSiteItem, index: number) => {
        const { activeIndex, autoplayEnabled, progressKey } = this.state
        const { gallerySeconds } = this.props
        const isActive = index === activeIndex
        const isActiveVideo = isActive && this.isVideoItem(item)
        const showFullIndicator = !autoplayEnabled && isActive && !isActiveVideo
        const durationSec = autoplayEnabled && isActive && !isActiveVideo && gallerySeconds > 0 ? gallerySeconds : 0
        const meta = this.formatMeta(item)

        return (
            <button
                key={item.itemId || index}
                type="button"
                className={classNames("hotStoriesStripItem", { active: isActive })}
                onClick={() => this.onSelectItem(index)}
            >
                <div className="hotStoriesStripTitle">{item.title}</div>
                {meta && <div className="hotStoriesStripMeta">{meta}</div>}
                <div
                    className="hotStoriesProgressTrack"
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                >
                    {isActiveVideo &&
                        <div
                            key={progressKey}
                            ref={this.setVideoProgressFill}
                            className="hotStoriesProgressFill synced"
                            style={{ width: "0%" }}
                        />}
                    {showFullIndicator &&
                        <div
                            key={progressKey}
                            className="hotStoriesProgressFill synced"
                            style={{ width: "100%" }}
                        />}
                    {durationSec > 0 &&
                        <div
                            key={progressKey}
                            className="hotStoriesProgressFill"
                            style={{ animationDuration: `${durationSec}s` }}
                        />}
                </div>
            </button>
        )
    }

    public render() {
        const { items, backgroundColorComponent } = this.props
        const { activeIndex } = this.state
        if (!items || items.length === 0) {
            return null
        }
        const activeItem = items[activeIndex]
        const showArrows = items.length > 1 && items.length !== 3
        const showPrevArrow = showArrows && activeIndex > 0
        const showNextArrow = showArrows && activeIndex < items.length - 1
        let stripItems: { item: HotStoriesSiteItem, index: number }[]
        if (items.length <= 3) {
            stripItems = items.map((item, index) => ({ item, index }))
        } else {
            let start = activeIndex - 2
            if (start < 0) {
                start = 0
            }
            if (start + 3 > items.length) {
                start = items.length - 3
            }
            stripItems = items.slice(start, start + 3).map((item, i) => ({ item, index: start + i }))
        }

        return (
            <div className="HotStoriesFront" style={{ backgroundColor: backgroundColorComponent || HOT_STORIES_BG }}>
                {this.renderHero(activeItem)}
                <div className="hotStoriesStrip">
                    {showPrevArrow &&
                        <button type="button" className="hotStoriesArrow hotStoriesArrowPrev" aria-label="previous" onClick={this.onPrev} />}
                    <div className={classNames("hotStoriesStripList", { noArrows: !showArrows })}>
                        {stripItems.map(({ item, index }) => this.renderStripItem(item, index))}
                    </div>
                    {showNextArrow &&
                        <button type="button" className="hotStoriesArrow hotStoriesArrowNext" aria-label="next" onClick={this.onNext} />}
                </div>
            </div>
        )
    }
}
